import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ChevronLeft, ChevronRight, Play, Download, 
    TrendingUp, Clock, Shield, Users, CheckCircle,
    Building, PoundSterling, Zap, Target, Award,
    BarChart3, FileText, Lock, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const slides = [
    {
        id: 1,
        type: 'title',
        title: 'FendanaCor',
        subtitle: 'Revolutionising NHS Orthopaedic Referrals',
        tagline: 'Evidence-Based Triage for Better Patient Outcomes',
        background: 'gradient-primary'
    },
    {
        id: 2,
        type: 'problem',
        title: 'The Problem',
        points: [
            { icon: Clock, text: 'NHS orthopaedic waiting lists exceed 6 million patients', highlight: '6M+' },
            { icon: Users, text: 'Up to 40% of referrals fail to meet secondary care criteria', highlight: '40%' },
            { icon: PoundSterling, text: 'Administrative burden costs NHS £2.3B annually', highlight: '£2.3B' },
            { icon: FileText, text: 'Paper-based systems cause delays and lost referrals', highlight: 'Inefficient' }
        ]
    },
    {
        id: 3,
        type: 'solution',
        title: 'Our Solution',
        subtitle: 'FendanaCor: Smart Orthopaedic Referral Triage',
        features: [
            { icon: Zap, title: 'Automated Screening', desc: 'BOA, NICE, NHS guidelines integrated' },
            { icon: Target, title: 'Urgency Scoring', desc: 'Evidence-based prioritisation (0-100 scale)' },
            { icon: Clock, title: 'Instant Feedback', desc: 'Real-time screening with actionable recommendations' },
            { icon: Shield, title: 'CQC Ready', desc: 'Complete audit trail for compliance' }
        ]
    },
    {
        id: 4,
        type: 'benefits',
        title: 'Key Benefits',
        sections: [
            {
                title: 'For NHS Trusts',
                icon: Building,
                items: [
                    'Reduce waiting lists by filtering inappropriate referrals',
                    'Eliminate secretarial triage burden',
                    'Real-time performance monitoring',
                    'Automatic appointment scheduling'
                ]
            },
            {
                title: 'For Patients',
                icon: Heart,
                items: [
                    'Right care at the right time',
                    'Clear feedback on referral status',
                    'Reduced waiting times for appropriate cases',
                    'Better preparation for consultations'
                ]
            }
        ]
    },
    {
        id: 5,
        type: 'workflow',
        title: 'Seamless Referral Flow',
        steps: [
            { num: 1, title: 'GP Creates Referral', desc: 'Guided form with clinical questionnaires' },
            { num: 2, title: 'Auto Screening', desc: 'Guidelines-based validation in seconds' },
            { num: 3, title: 'Urgency Scoring', desc: 'AI-enhanced prioritisation' },
            { num: 4, title: 'Auto Scheduling', desc: 'Fair distribution to consultants' },
            { num: 5, title: 'Appointment Letter', desc: 'Automatic patient notification' }
        ]
    },
    {
        id: 6,
        type: 'savings',
        title: 'Cost Savings Analysis',
        metrics: [
            { label: 'Triage Time Saved', value: '90%', desc: 'From 15 mins to <2 mins per referral' },
            { label: 'Admin Cost Reduction', value: '£45K', desc: 'Per consultant per year' },
            { label: 'Inappropriate Referrals', value: '-40%', desc: 'Filtered at source with guidance' },
            { label: 'Wait List Reduction', value: '25%', desc: 'By ensuring referral readiness' }
        ]
    },
    {
        id: 7,
        type: 'features',
        title: 'Platform Features',
        features: [
            { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Real-time metrics, wait times, acceptance rates' },
            { icon: FileText, title: 'Pre-Clinic Notes', desc: 'AI-generated comprehensive medical summaries' },
            { icon: Lock, title: 'Full Audit Trail', desc: 'Every action logged for CQC compliance' },
            { icon: Zap, title: 'AI Integration', desc: 'GPT-powered guidelines chatbot and summaries' },
            { icon: Users, title: 'Multi-Role Access', desc: 'GPs, Consultants, Managers, Admins' },
            { icon: Target, title: 'Subspecialty Matching', desc: 'Smart routing to appropriate consultants' }
        ]
    },
    {
        id: 8,
        type: 'compliance',
        title: 'Governance & Compliance',
        points: [
            { icon: Shield, title: 'NHS Data Protection', desc: 'Fully compliant with NHS IG requirements' },
            { icon: FileText, title: 'CQC Ready Audits', desc: 'Export-ready audit trails in CSV/PDF' },
            { icon: Lock, title: 'Role-Based Access', desc: 'Granular permissions by user type' },
            { icon: CheckCircle, title: 'Clinical Guidelines', desc: 'BOA, NICE, BESS, BOFAS, BSSH integrated' }
        ]
    },
    {
        id: 9,
        type: 'market',
        title: 'Market Opportunity',
        stats: [
            { value: '£15B', label: 'UK Orthopaedic Market' },
            { value: '230+', label: 'NHS Trusts' },
            { value: '6M+', label: 'Waiting List Patients' },
            { value: '2,500+', label: 'Orthopaedic Consultants' }
        ],
        text: 'FendanaCor addresses a critical NHS challenge with proven technology and clear ROI'
    },
    {
        id: 10,
        type: 'roadmap',
        title: 'Development Roadmap',
        phases: [
            { phase: 'Q1 2026', title: 'Platform Launch', items: ['Core referral system', 'Auto-scheduling', 'Analytics'], status: 'complete' },
            { phase: 'Q2 2026', title: 'NHS Integration', items: ['e-RS connection', 'PDS integration', 'Multi-trust pilot'], status: 'current' },
            { phase: 'Q3 2026', title: 'Scale', items: ['National rollout', 'Mobile apps', 'API marketplace'], status: 'planned' },
            { phase: 'Q4 2026', title: 'Expand', items: ['Other specialties', 'International markets', 'AI enhancements'], status: 'planned' }
        ]
    },
    {
        id: 11,
        type: 'ask',
        title: 'Investment Opportunity',
        seeking: '£2M Seed Round',
        use: [
            { label: 'Product Development', percent: 40 },
            { label: 'NHS Sales & Partnerships', percent: 30 },
            { label: 'Clinical Validation Studies', percent: 20 },
            { label: 'Operations', percent: 10 }
        ],
        cta: 'Join us in transforming NHS orthopaedic care'
    },
    {
        id: 12,
        type: 'contact',
        title: 'Thank You',
        subtitle: 'Questions?',
        contact: {
            email: 'investors@fendanacor.com',
            website: 'www.fendanacor.com',
            demo: 'Request a live demonstration'
        }
    }
];

export default function InvestorPitchPage() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const slide = slides[currentSlide];

    const renderSlide = () => {
        switch (slide.type) {
            case 'title':
                return (
                    <div className="h-full flex flex-col items-center justify-center text-center text-white p-12">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <img src="/logo.png" alt="FendanaCor" className="w-32 h-32 mx-auto mb-8 rounded-2xl shadow-2xl" />
                            <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {slide.title}
                            </h1>
                            <p className="text-2xl mb-4 text-teal-100">{slide.subtitle}</p>
                            <p className="text-lg text-teal-200">{slide.tagline}</p>
                        </motion.div>
                    </div>
                );

            case 'problem':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-2 gap-6">
                            {slide.points.map((point, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ x: -50, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.2 }}
                                    className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg"
                                >
                                    <div className="flex items-start gap-4">
                                        <point.icon className="w-8 h-8 text-red-500 flex-shrink-0" />
                                        <div>
                                            <span className="text-3xl font-bold text-red-600">{point.highlight}</span>
                                            <p className="text-slate-700 mt-2">{point.text}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'solution':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-2">{slide.title}</h2>
                        <p className="text-xl text-teal-600 mb-8">{slide.subtitle}</p>
                        <div className="grid grid-cols-2 gap-6">
                            {slide.features.map((feature, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="bg-white border border-teal-200 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                                >
                                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                                        <feature.icon className="w-6 h-6 text-teal-600" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                                    <p className="text-slate-600">{feature.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'benefits':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-2 gap-8">
                            {slide.sections.map((section, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ x: i === 0 ? -50 : 50, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-gradient-to-br from-teal-50 to-white p-6 rounded-xl border border-teal-100"
                                >
                                    <div className="flex items-center gap-3 mb-4">
                                        <section.icon className="w-8 h-8 text-teal-600" />
                                        <h3 className="text-2xl font-semibold text-slate-900">{section.title}</h3>
                                    </div>
                                    <ul className="space-y-3">
                                        {section.items.map((item, j) => (
                                            <li key={j} className="flex items-start gap-2">
                                                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-slate-700">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'workflow':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8 text-center">{slide.title}</h2>
                        <div className="flex items-center justify-between">
                            {slide.steps.map((step, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="flex flex-col items-center text-center"
                                >
                                    <div className="w-16 h-16 rounded-full bg-teal-600 text-white flex items-center justify-center text-2xl font-bold mb-3">
                                        {step.num}
                                    </div>
                                    <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                                    <p className="text-sm text-slate-600 max-w-[120px]">{step.desc}</p>
                                    {i < slide.steps.length - 1 && (
                                        <div className="absolute" style={{ left: `${(i + 1) * 20 - 2}%`, top: '45%' }}>
                                            <ChevronRight className="w-8 h-8 text-teal-300" />
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'savings':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-4 gap-6">
                            {slide.metrics.map((metric, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 rounded-xl text-center"
                                >
                                    <div className="text-4xl font-bold mb-2">{metric.value}</div>
                                    <div className="font-semibold mb-2">{metric.label}</div>
                                    <div className="text-sm text-teal-100">{metric.desc}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'features':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-3 gap-4">
                            {slide.features.map((feature, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="bg-white border border-slate-200 p-5 rounded-lg hover:border-teal-300 transition-colors"
                                >
                                    <feature.icon className="w-8 h-8 text-teal-600 mb-3" />
                                    <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                                    <p className="text-sm text-slate-600">{feature.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'compliance':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-2 gap-6">
                            {slide.points.map((point, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ x: i % 2 === 0 ? -30 : 30, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="flex items-start gap-4 bg-blue-50 p-6 rounded-xl"
                                >
                                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <point.icon className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-900 mb-1">{point.title}</h3>
                                        <p className="text-slate-600">{point.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'market':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-4 gap-6 mb-8">
                            {slide.stats.map((stat, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="text-center p-6 bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-xl"
                                >
                                    <div className="text-4xl font-bold mb-2">{stat.value}</div>
                                    <div className="text-purple-100">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="text-xl text-center text-slate-700 bg-slate-100 p-6 rounded-xl"
                        >
                            {slide.text}
                        </motion.p>
                    </div>
                );

            case 'roadmap':
                return (
                    <div className="h-full p-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-8">{slide.title}</h2>
                        <div className="grid grid-cols-4 gap-4">
                            {slide.phases.map((phase, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className={`p-5 rounded-xl border-2 ${
                                        phase.status === 'complete' ? 'bg-emerald-50 border-emerald-500' :
                                        phase.status === 'current' ? 'bg-teal-50 border-teal-500' :
                                        'bg-slate-50 border-slate-300'
                                    }`}
                                >
                                    <div className={`text-sm font-bold mb-2 ${
                                        phase.status === 'complete' ? 'text-emerald-600' :
                                        phase.status === 'current' ? 'text-teal-600' :
                                        'text-slate-500'
                                    }`}>{phase.phase}</div>
                                    <h3 className="font-semibold text-slate-900 mb-3">{phase.title}</h3>
                                    <ul className="space-y-1">
                                        {phase.items.map((item, j) => (
                                            <li key={j} className="text-sm text-slate-600 flex items-center gap-2">
                                                <div className={`w-1.5 h-1.5 rounded-full ${
                                                    phase.status === 'complete' ? 'bg-emerald-500' :
                                                    phase.status === 'current' ? 'bg-teal-500' :
                                                    'bg-slate-400'
                                                }`} />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                );

            case 'ask':
                return (
                    <div className="h-full p-12 bg-gradient-to-br from-teal-600 to-emerald-700 text-white">
                        <h2 className="text-4xl font-bold mb-4">{slide.title}</h2>
                        <div className="text-6xl font-bold mb-8">{slide.seeking}</div>
                        <div className="grid grid-cols-4 gap-4 mb-8">
                            {slide.use.map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: i * 0.15 }}
                                    className="bg-white/20 p-4 rounded-lg text-center"
                                >
                                    <div className="text-3xl font-bold">{item.percent}%</div>
                                    <div className="text-sm text-teal-100">{item.label}</div>
                                </motion.div>
                            ))}
                        </div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="text-2xl text-center text-teal-100"
                        >
                            {slide.cta}
                        </motion.p>
                    </div>
                );

            case 'contact':
                return (
                    <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                        >
                            <img src="/logo.png" alt="FendanaCor" className="w-24 h-24 mx-auto mb-6 rounded-xl" />
                            <h2 className="text-5xl font-bold mb-2">{slide.title}</h2>
                            <p className="text-2xl text-slate-400 mb-8">{slide.subtitle}</p>
                            <div className="space-y-3 text-lg">
                                <p className="text-teal-400">{slide.contact.email}</p>
                                <p className="text-slate-400">{slide.contact.website}</p>
                                <p className="text-emerald-400 font-semibold mt-4">{slide.contact.demo}</p>
                            </div>
                        </motion.div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="investor-pitch-page">
            {/* Main Slide Area */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className={`w-full max-w-6xl aspect-video rounded-2xl shadow-2xl overflow-hidden ${
                    slide.type === 'title' ? 'gradient-primary' : 
                    slide.type === 'ask' ? '' : 
                    slide.type === 'contact' ? '' : 'bg-white'
                }`}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="h-full"
                        >
                            {renderSlide()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Navigation Controls */}
            <div className="bg-white border-t border-slate-200 p-4">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={prevSlide}
                            disabled={currentSlide === 0}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={nextSlide}
                            disabled={currentSlide === slides.length - 1}
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>

                    {/* Slide Indicators */}
                    <div className="flex items-center gap-1">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goToSlide(i)}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                    i === currentSlide ? 'bg-teal-600' : 'bg-slate-300 hover:bg-slate-400'
                                }`}
                            />
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">
                            {currentSlide + 1} / {slides.length}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
